import React, { memo, useCallback, useMemo } from 'react';
import { cn } from '@crabs-ai/ds-react';
import { TransitionChopFill } from '@crabs-ai/web-icons';
import {
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HORIZONTAL_PADDING,
} from '../../constants';
import { Z_INDEX_TRANSITION_ITEM } from '../../z-indices';
import { TransitionItem } from '../../items/transition/transition-item-type';
import {
  getItemLeftOffset,
  getItemRoundedPosition,
  getItemWidth,
} from '../../utils/position-utils';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../context-menu';
import { TimelineTransitionExtendHandles } from './timeline-transition-extend-handles';
import { TimelineTrackAndLayout } from '../utils/drag/calculate-track-heights';

interface TimelineTransitionItemProps {
  transition: TransitionItem;
  timelineWidth: number;
  visibleFrames: number;
  tracksLayout: TimelineTrackAndLayout[];
  isSelected: boolean;
  onClick: (e: React.MouseEvent, transitionId: string) => void;
  onDelete: (transitionId: string) => void;
}

// 아이콘 크기
const ICON_SIZE = 24;

const TimelineTransitionItemUnmemoized: React.FC<
  TimelineTransitionItemProps
> = ({
  transition,
  timelineWidth,
  visibleFrames,
  tracksLayout,
  isSelected,
  onClick,
  onDelete,
}) => {
  // 트랙 레이아웃 찾기
  const trackLayout = tracksLayout.find(
    (layout) => layout.track.id === transition.trackId,
  );

  // X 위치 계산 (트랜지션 시작 프레임 기준)
  const transitionLeft = getItemLeftOffset({
    timelineWidth,
    totalDurationInFrames: visibleFrames,
    from: transition.from,
  });

  // 너비 계산 (duration 기반)
  const transitionWidth = getItemWidth({
    itemDurationInFrames: transition.durationInFrames,
    timelineWidth,
    totalDurationInFrames: visibleFrames,
  });

  // 반올림 처리
  const { roundedLeft, width } = getItemRoundedPosition(
    transitionLeft,
    transitionWidth,
  );

  // 트랙 높이와 동일하게 설정
  const transitionHeight = trackLayout?.height ?? 0;
  const topOffset = trackLayout?.top ?? 0;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onClick(e, transition.id);
    },
    [onClick, transition.id],
  );

  const handleDelete = useCallback(
    (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete(transition.id);
    },
    [onDelete, transition.id],
  );

  const handleContextMenuPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
    },
    [],
  );

  // 컨테이너 스타일
  const containerStyle: React.CSSProperties = useMemo(() => {
    return {
      position: 'absolute',
      left: roundedLeft + TIMELINE_HORIZONTAL_PADDING,
      top: TIMELINE_HEADER_HEIGHT + topOffset,
      width: Math.max(width, 4),
      height: transitionHeight,
      zIndex: Z_INDEX_TRANSITION_ITEM,
    };
  }, [roundedLeft, topOffset, width, transitionHeight, isSelected]);

  if (!trackLayout) {
    return null;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className={cn(
            'pointer-events-auto cursor-pointer',
            'hover:brightness-105',
          )}
          style={containerStyle}
          onClick={handleClick}
          onPointerDown={(e) => e.stopPropagation()}
          title={`${transition.transitionType} transition (${transition.durationInFrames} frames)`}
        >
          {/* 메인 트랜지션 영역 */}
          <div
            className={cn(
              'flex h-full w-full items-center justify-center rounded bg-sys-style-overlay-inverse-dark',
              isSelected && 'border-1 border-sys-cont-none-dark',
            )}
          >
            {/* 트랜지션 아이콘 */}
            <TransitionChopFill
              width={ICON_SIZE}
              height={ICON_SIZE}
              className="[&_path]:fill-sys-cont-none-dark"
            />
          </div>

          {/* 리사이즈 핸들 */}
          <TimelineTransitionExtendHandles
            transition={transition}
            timelineWidth={timelineWidth}
            width={width}
            height={transitionHeight}
          />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          className="flex items-center gap-3 text-red-400 hover:text-red-300"
          onSelect={handleDelete}
          onPointerDown={handleContextMenuPointerDown}
        >
          Delete Transition
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

export const TimelineTransitionItem = memo(TimelineTransitionItemUnmemoized);
